/**
 * Mass Role - Validate Pillar
 *
 * Validação semântica de valores de massa.
 * Zero dependências externas - pode ser usado standalone.
 *
 * @example
 * import { validateMass, isValidMass } from '@attrx/role-morphic/mass/validate';
 *
 * validateMass(100);   // { valid: true, errors: [] }
 * validateMass(-5);    // { valid: false, errors: ['Mass cannot be negative'] }
 * isValidMass(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface MassValidationConfig {
  min?: number;
  max?: number;
  integer?: boolean;
  minError?: string;
  maxError?: string;
}

// =============================================================================
// CONFIG
// =============================================================================

/**
 * Configuração padrão de validação para massa
 */
export const MASS_VALIDATION_CONFIG: MassValidationConfig = {
  min: 0,
  minError: "Mass cannot be negative",
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de massa
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns Resultado da validação com erros se houver
 *
 * @example
 * validateMass(100);              // { valid: true, errors: [] }
 * validateMass(-5);               // { valid: false, errors: ['Mass cannot be negative'] }
 * validateMass(150, { max: 100 }) // { valid: false, errors: ['Value must be at most 100'] }
 */
export function validateMass(
  value: unknown,
  config: MassValidationConfig = MASS_VALIDATION_CONFIG
): ValidationResult {
  const errors: string[] = [];

  // Type check
  if (typeof value !== "number") {
    return { valid: false, errors: ["Value must be a number"] };
  }

  // Finite check
  if (!Number.isFinite(value)) {
    return { valid: false, errors: ["Value must be finite"] };
  }

  // Min check
  if (config.min !== undefined && value < config.min) {
    const msg = config.minError ?? `Value must be at least ${config.min}`;
    errors.push(msg);
  }

  // Max check
  if (config.max !== undefined && value > config.max) {
    const msg = config.maxError ?? `Value must be at most ${config.max}`;
    errors.push(msg);
  }

  // Integer check
  if (config.integer && !Number.isInteger(value)) {
    errors.push("Value must be an integer");
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Verifica se um valor de massa é válido
 *
 * @param value - Valor a ser validado
 * @param config - Configuração customizada (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidMass(100);  // true
 * isValidMass(-5);   // false
 */
export function isValidMass(
  value: unknown,
  config: MassValidationConfig = MASS_VALIDATION_CONFIG
): boolean {
  return validateMass(value, config).valid;
}
